/**
 * kmlLoader.ts
 * -----------------------------------------------------------------------------
 * Fetches a project's KML boundary file and hands back a parsed, render-ready
 * result — transparently supporting both plain .kml (XML text) and .kmz
 * (a zipped .kml plus embedded image assets), which is what Google Earth
 * Pro produces by default when a project is exported/saved.
 *
 * Detection is done by sniffing the ZIP magic bytes rather than trusting the
 * stored filename/extension, so it works regardless of what the client
 * actually uploaded.
 * -----------------------------------------------------------------------------
 */

import JSZip from "jszip";
import { parseKmlDocument } from "./kmlParser";
import type { KmlFolder, KmlOverlay, KmlParseResult, KmlStyle } from "./kmlParser";

export interface LoadedKml {
  parsed: KmlParseResult;
  /** Revokes every blob URL created for KMZ-embedded assets. Call before the next load / on unmount. */
  revoke: () => void;
}

const ZIP_MAGIC = [0x50, 0x4b, 0x03, 0x04]; // "PK\x03\x04"

function isZip(bytes: Uint8Array): boolean {
  return bytes.length >= 4 && ZIP_MAGIC.every((b, i) => bytes[i] === b);
}

/** True for hrefs that are already usable as-is (absolute URL, protocol-relative, data:, blob:, etc). */
function isResolvedHref(href: string): boolean {
  return /^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith("//");
}

/** Extracts the root .kml text plus a filename -> blob-URL map for every other archive entry. */
async function extractKmzEntry(zip: JSZip): Promise<{ kmlText: string; assets: Map<string, string> }> {
  const entries = Object.values(zip.files).filter((f) => !f.dir);
  const kmlEntries = entries.filter((f) => f.name.toLowerCase().endsWith(".kml"));
  if (kmlEntries.length === 0) {
    throw new Error("KMZ archive does not contain a .kml file");
  }

  // Prefer the conventional "doc.kml", then any root-level *.kml, then the first found.
  const kmlEntry =
    kmlEntries.find((f) => f.name.toLowerCase() === "doc.kml") ||
    kmlEntries.find((f) => !f.name.includes("/")) ||
    kmlEntries[0];

  const kmlText = await kmlEntry.async("string");

  const assets = new Map<string, string>();
  for (const entry of entries) {
    if (entry === kmlEntry) continue;
    const blob = await entry.async("blob");
    assets.set(entry.name.replace(/^\.?\//, ""), URL.createObjectURL(blob));
  }

  return { kmlText, assets };
}

/**
 * Resolves a single href against either the KMZ asset map (by exact path,
 * falling back to a basename match) or, for a plain .kml, against the
 * source URL. Already-absolute hrefs pass through untouched.
 */
function resolveHref(href: string | null, assets: Map<string, string> | null, baseUrl: string): string | null {
  if (!href) return href;
  if (isResolvedHref(href)) return href;

  if (assets) {
    const normalized = href.replace(/^\.?\//, "");
    if (assets.has(normalized)) return assets.get(normalized) as string;
    const basename = normalized.split("/").pop();
    const match = Array.from(assets.entries()).find(([name]) => name.split("/").pop() === basename);
    return match ? match[1] : href;
  }

  try {
    return new URL(href, baseUrl).toString();
  } catch {
    return href;
  }
}

function resolveStyleHrefs(style: KmlStyle | null, resolve: (href: string | null) => string | null): void {
  if (style && style.iconStyle) {
    style.iconStyle.iconHref = resolve(style.iconStyle.iconHref);
  }
}

function resolveOverlayHrefs(overlay: KmlOverlay, resolve: (href: string | null) => string | null): void {
  overlay.icon.href = resolve(overlay.icon.href);
}

function resolveFolderHrefs(folder: KmlFolder, resolve: (href: string | null) => string | null): void {
  folder.placemarks.forEach((p) => resolveStyleHrefs(p.style, resolve));
  folder.overlays.forEach((o) => resolveOverlayHrefs(o, resolve));
  folder.folders.forEach((f) => resolveFolderHrefs(f, resolve));
}

/**
 * Rewrites every relative icon/overlay href found anywhere in the parsed
 * result (style registry, StyleMap-inline styles, every placemark's resolved
 * style, and every overlay including ones nested in folders) to an absolute
 * URL — a blob URL for KMZ-embedded assets, or resolved against the source
 * URL for a plain .kml referencing a relative sibling image.
 */
function resolveKmlAssetHrefs(parsed: KmlParseResult, assets: Map<string, string> | null, baseUrl: string): void {
  const resolve = (href: string | null) => resolveHref(href, assets, baseUrl);

  Object.values(parsed.styles).forEach((style) => resolveStyleHrefs(style, resolve));
  Object.values(parsed.styleMaps).forEach((styleMap) => {
    styleMap.pairs.forEach((pair) => resolveStyleHrefs(pair.style, resolve));
  });

  parsed.placemarks.forEach((p) => resolveStyleHrefs(p.style, resolve));
  parsed.folders.forEach((folder) => resolveFolderHrefs(folder, resolve));
  parsed.overlays.forEach((overlay) => resolveOverlayHrefs(overlay, resolve));
}

/**
 * Fetches, unzips (if needed) and parses the KML file at `url`. Safe to call
 * for both a plain .kml and a .kmz regardless of the stored filename.
 */
export async function loadKmlSource(url: string): Promise<LoadedKml> {
  const response = await fetch(url);
  const buffer = await response.arrayBuffer();
  const bytes = new Uint8Array(buffer);

  let kmlText: string;
  let assets: Map<string, string> | null = null;

  if (isZip(bytes)) {
    const zip = await JSZip.loadAsync(buffer);
    const extracted = await extractKmzEntry(zip);
    kmlText = extracted.kmlText;
    assets = extracted.assets;
  } else {
    kmlText = new TextDecoder("utf-8").decode(bytes);
  }

  const parsed = parseKmlDocument(kmlText);
  resolveKmlAssetHrefs(parsed, assets, url);

  const blobUrls = assets ? Array.from(assets.values()) : [];
  return {
    parsed,
    revoke: () => {
      blobUrls.forEach((blobUrl) => URL.revokeObjectURL(blobUrl));
    },
  };
}
